import React, { useEffect, useState } from 'react';
import { CardPayment, initMercadoPago } from '@mercadopago/sdk-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { vipService, VipCardPaymentBrickPayload } from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { Spinner } from 'react-bootstrap';

const publicKey = process.env.REACT_APP_MERCADOPAGO_PUBLIC_KEY ?? '';

type Props = {
  amount: number;
  payerEmail?: string;
  onSuccess: () => void | Promise<void>;
};

const VipCardPaymentBrick: React.FC<Props> = ({ amount, payerEmail, onSuccess }) => {
  const { t } = useSafeTranslation();
  const [sdkReady, setSdkReady] = useState(false);

  useEffect(() => {
    if (!publicKey) return;
    initMercadoPago(publicKey, { locale: 'pt-BR', siteId: 'MLB' });
    setSdkReady(true);
  }, []);

  if (!publicKey) {
    return (
      <div className="alert alert-warning mb-0">
        {t(
          'vip.mpKeyMissing',
          'Set REACT_APP_MERCADOPAGO_PUBLIC_KEY in your environment to enable Mercado Pago checkout.'
        )}
      </div>
    );
  }

  if (!sdkReady) {
    return (
      <div className="d-flex align-items-center justify-content-center py-4 text-muted gap-2">
        <Spinner animation="border" size="sm" />
        {t('vip.mpLoading', 'Loading payment form…')}
      </div>
    );
  }

  return (
    <div className="vip-mp-card-brick">
      <CardPayment
        locale="pt-BR"
        initialization={{
          amount,
          payer: payerEmail ? { email: payerEmail } : undefined,
        }}
        customization={{
          paymentMethods: {
            maxInstallments: 12,
            minInstallments: 1,
          },
        }}
        onSubmit={async (cardFormData: VipCardPaymentBrickPayload) => {
          try {
            const data = await vipService.processCardPayment(cardFormData);
            if (data.token) {
              localStorage.setItem('authToken', data.token);
            }
            toast.success(t('vip.paymentSuccess', 'VIP activated!'));
            await onSuccess();
          } catch (err: unknown) {
            let msg = t('vip.paymentFailed', 'Payment failed. Check your card and try again.');
            if (axios.isAxiosError(err) && err.response?.data) {
              const d = err.response.data as { message?: string };
              if (typeof d.message === 'string') msg = d.message;
            }
            toast.error(msg);
            throw err;
          }
        }}
        onError={() => {
          toast.error(t('vip.paymentBrickError', 'Payment form error. Try again.'));
        }}
      />
    </div>
  );
};

export default VipCardPaymentBrick;
